function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo } from 'react';
import { Background, EdgeTypes, type NodeTypes, type OnNodeDrag, PanOnScrollMode, ReactFlow, SelectionMode, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useShallow } from 'zustand/react/shallow';
import { QueryClientProvider } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { queryClient } from 'common/react-query/query-client';
import { useCurrentUser } from 'common/contexts/react/current-user-context';
import styles from './index.module.styl';
import { MetricNode } from './nodes/metric-node';
import { TextNode } from './nodes/text-node';
import { CorrelationEdge } from './edges/correlation-edge';
import { ColoredMarkerDefinitions } from './edges/correlation-edge/marker-definitions';
import { PanelControls } from './panels';
import { AppState, useCanvasStore } from './store';
import { CanvasProps } from './types';
import { CanvasHeader } from './header';
import { AppNode } from './nodes/types';
import { CursorPresence } from './cursor-presence';
import { SimpleEdge } from './edges/simple-edge';
import { AppEdge } from './edges/types';
import { StickyNoteNode } from './nodes/sticky-note-node';
import { MAX_ZOOM, MIN_ZOOM } from './constants';
import { PureTextNode } from './nodes/pure-text-node';
const DEMO_ROOM_ID = "metric-tree-12345";
const DELETE_KEY_CODE = ["Delete", "Backspace"];
const nodeTypes: NodeTypes = {
  metric: MetricNode,
  text: TextNode,
  stickyNote: StickyNoteNode,
  pureText: PureTextNode
};
const edgeTypes: EdgeTypes = {
  correlation: CorrelationEdge,
  simple: SimpleEdge
};
const selector = (state: AppState) => {
  var _state$canvas;
  return {
    isViewOnly: !((_state$canvas = state.canvas) !== null && _state$canvas !== void 0 && _state$canvas.can_update_basic),
    nodes: state.nodes,
    edges: state.edges,
    ghostNode: state.ghostNode,
    localSelectedNodeIds: state.localSelectedNodeIds,
    setLocalSelectedNodeIds: state.setLocalSelectedNodeIds,
    localSelectedEdgeIds: state.localSelectedEdgeIds,
    setLocalSelectedEdgeIds: state.setLocalSelectedEdgeIds,
    hydrate: state.hydrate,
    stageGhostNode: state.stageGhostNode,
    commitGhostNode: state.commitGhostNode,
    updateGhostNodePosition: state.updateGhostNodePosition,
    duplicateNode: state.duplicateNode,
    deleteNode: state.deleteNode,
    onNodesChange: state.onNodesChange,
    onEdgesChange: state.onEdgesChange,
    onConnect: state.onConnect,
    onReconnect: state.onReconnect,
    setActiveMetricNodeId: state.setActiveMetricNodeId,
    setActiveEdge: state.setActiveEdge,
    setCursor: state.setCursor,
    init: state.init,
    destroy: state.destroy,
    isStorageLoading: state.liveblocks.isStorageLoading,
    liveblocksRoom: state.liveblocks.room
  };
};
export function Canvas(props: CanvasProps) {
  var _currentUser$get$valu;
  const {
    isStorageLoading,
    liveblocksRoom,
    isViewOnly,
    nodes,
    edges,
    hydrate,
    ghostNode,
    localSelectedNodeIds,
    localSelectedEdgeIds,
    setLocalSelectedNodeIds,
    setLocalSelectedEdgeIds,
    stageGhostNode,
    commitGhostNode,
    updateGhostNodePosition,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onReconnect,
    deleteNode,
    duplicateNode,
    setActiveMetricNodeId,
    setActiveEdge,
    setCursor,
    init,
    destroy
  } = useCanvasStore(useShallow(selector));
  const {
    screenToFlowPosition
  } = useReactFlow();
  const {
    hash
  } = useLocation();
  const [searchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const hasGhostNode = Boolean(ghostNode);
  const currentUserName = (_currentUser$get$valu = currentUser.get().value) === null || _currentUser$get$valu === void 0 ? void 0 : _currentUser$get$valu.name;
  const localNodes: AppNode[] = useMemo(() => {
    return nodes.map(node => _objectSpread(_objectSpread({}, node), {}, {
      selected: localSelectedNodeIds.includes(node.id)
    }));
  }, [nodes, localSelectedNodeIds]);
  const localEdges: AppEdge[] = useMemo(() => {
    return edges.map(edge => _objectSpread(_objectSpread({}, edge), {}, {
      selected: localSelectedEdgeIds.includes(edge.id)
    }));
  }, [edges, localSelectedEdgeIds]);
  useEffect(() => {
    const roomId = props.projectId ? "project_".concat(props.projectId, ":canvas_").concat(props.canvasId) : DEMO_ROOM_ID;
    init(roomId);
    return () => {
      destroy();
    };
  }, [props.projectId, props.canvasId, init, destroy]);

  // note that we are not listing to hash change here. This should only happen when user freshly load the canvas
  useEffect(() => {
    hydrate(hash.slice(1));
  }, []);

  // only set active node on page load
  useEffect(() => {
    if (searchParams.get("active-node")) {
      setActiveMetricNodeId(searchParams.get("active-node"));
    }
  }, []);
  useEffect(() => {
    return liveblocksRoom === null || liveblocksRoom === void 0 ? void 0 : liveblocksRoom.subscribe("error", error => {
      props.onError(error);
    });
  }, [liveblocksRoom, props.onError]);
  const handlePaneClick = useCallback(() => {
    // When clicking on the pane, we want to close the query builder panel
    if (!hasGhostNode) {
      // Added this check to prevent re-rendering the whole canvas on pane click when there is no ghost node
      if (localSelectedNodeIds.length > 0) setLocalSelectedNodeIds([]); // clear selections
      if (localSelectedEdgeIds.length > 0) setLocalSelectedEdgeIds([]); // clear selections
      setActiveMetricNodeId(null);
      setActiveEdge(null);
    }
  }, [hasGhostNode, localSelectedNodeIds, localSelectedEdgeIds, setLocalSelectedNodeIds, setActiveMetricNodeId, setActiveEdge]);
  const handleNodeDragStart = useCallback<OnNodeDrag>((event, node) => {
    var _liveblocksRoom$histo;
    // need to pause history when start to drag because position changes too fast
    liveblocksRoom === null || liveblocksRoom === void 0 || (_liveblocksRoom$histo = liveblocksRoom.history) === null || _liveblocksRoom$histo === void 0 || _liveblocksRoom$histo.pause();
    // duplicating
    if (event.altKey) {
      duplicateNode(node.id, {
        x: node.position.x,
        y: node.position.y
      }, {
        duplicateEdges: true,
        setActiveOnDuplicatedNode: false,
        removeEdgesFromOriginalNode: true
      });
    }
    // When dragging a node, we want to close the query builder panel
    setActiveMetricNodeId(null);
  }, [setActiveMetricNodeId, liveblocksRoom, duplicateNode]);
  const handleNodeDragStop = useCallback(() => {
    var _liveblocksRoom$histo2;
    // When dragging a node, we want to close the query builder panel
    liveblocksRoom === null || liveblocksRoom === void 0 || (_liveblocksRoom$histo2 = liveblocksRoom.history) === null || _liveblocksRoom$histo2 === void 0 || _liveblocksRoom$histo2.resume();
  }, [liveblocksRoom]);
  const handleNodeClick = useCallback(() => {
    // onNodeClick need to handle ghost node commited to the node list
    if (hasGhostNode) {
      commitGhostNode({
        currentUserName
      });
    } else {
      setActiveEdge(null);
    }
  }, [hasGhostNode, commitGhostNode, currentUserName, setActiveEdge]);
  const handleEdgeClick = useCallback((_event: MouseEvent, edge: AppEdge) => {
    if (hasGhostNode) {
      commitGhostNode({
        currentUserName
      });
    } else {
      setActiveEdge(edge);
      setActiveMetricNodeId(null);
    }
  }, [hasGhostNode, setActiveMetricNodeId, setActiveEdge, commitGhostNode, currentUserName]);
  const handlePaneMouseMove = useCallback((event: MouseEvent) => {
    // Transform mouse position to canvas coordinates
    const canvasPosition = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY
    });

    // Update ghost node's position
    updateGhostNodePosition(canvasPosition);
  }, [screenToFlowPosition, updateGhostNodePosition]);
  const handleMouseMove = useCallback((event: MouseEvent) => {
    // Transform mouse position to canvas coordinates
    const canvasPosition = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY
    });
    setCursor({
      x: canvasPosition.x,
      y: canvasPosition.y
    });
  }, [setCursor, screenToFlowPosition]);
  const handleMouseLeave = useCallback(() => {
    setCursor(null);
  }, [setCursor]);
  const handleNodeDelete = useCallback((deletedNodes: AppNode[]) => {
    // This is fired if a user presses Delete for nodes inside ReactFlow. This handle makes
    // ReactFlow's built-in deletion to trigger zustand store updates to ensure state updates
    // are always in sync
    if (isViewOnly) return;
    deletedNodes.forEach(node => {
      deleteNode(node.id);
    });
  }, [isViewOnly, deleteNode]);

  // keyboard shortcuts need to be captured in event capture phase (document -> canvas)
  const handleKeyDownCapture = useCallback((event: globalThis.KeyboardEvent) => {
    // ctrl|command + z (undo)
    if (!isViewOnly && event.key === "z" && (event.ctrlKey || event.metaKey)) {
      var _liveblocksRoom$histo3;
      liveblocksRoom === null || liveblocksRoom === void 0 || (_liveblocksRoom$histo3 = liveblocksRoom.history) === null || _liveblocksRoom$histo3 === void 0 || _liveblocksRoom$histo3.undo();
      return;
    }
    // ctrl|command + shift + z (redo)
    if (!isViewOnly && event.key === "z" && (event.ctrlKey || event.metaKey) && event.shiftKey) {
      var _liveblocksRoom$histo4;
      liveblocksRoom === null || liveblocksRoom === void 0 || (_liveblocksRoom$histo4 = liveblocksRoom.history) === null || _liveblocksRoom$histo4 === void 0 || _liveblocksRoom$histo4.redo();
    }
  }, [isViewOnly, liveblocksRoom, stageGhostNode]);
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    var _event$target;
    // stop triggering delete node event
    if (isViewOnly && (event.key === "Delete" || event.key === "Backspace")) {
      event.stopPropagation();
      return;
    }

    // hack around to prevent normal input field typing to trigger keyboard shortcuts
    if (["MP-INPUT", "INPUT", "MP-TAG-INPUT", "MP-BREADCRUMBS"].includes((_event$target = event.target as HTMLElement) === null || _event$target === void 0 ? void 0 : _event$target.tagName)) {
      // don't trigger if input or textarea is focused
      return;
    }

    // t (add pure text node)
    if (!isViewOnly && event.key === "t") {
      stageGhostNode("pureText");
      return;
    }

    // i/o (add text node)
    if (!isViewOnly && (event.key === "i" || event.key === "o")) {
      stageGhostNode("text");
      return;
    }

    // m (add metric node)
    if (!isViewOnly && event.key === "m") {
      stageGhostNode("metric");
      return;
    }

    // n (add sticky note node)
    if (!isViewOnly && event.key === "n") {
      stageGhostNode("stickyNote");
    }
  }, [isViewOnly, stageGhostNode]);
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDownCapture, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDownCapture, true);
    };
  }, [handleKeyDownCapture]);
  if (isStorageLoading || !queryClient) {
    return <div className={styles.centered}>
        <SpinnerReact size={SpinnerSize.Large} />
      </div>;
  }
  return <div className={styles.reactFlowWrapper} onKeyDown={handleKeyDown} tabIndex={0} data-sentry-component="Canvas" data-sentry-source-file="index.tsx">
      <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="index.tsx">
        <ColoredMarkerDefinitions data-sentry-element="ColoredMarkerDefinitions" data-sentry-source-file="index.tsx" />
        <CanvasHeader canvasId={props.canvasId} projectId={props.projectId} workspaceId={props.workspaceId} userId={props.userId} data-sentry-element="CanvasHeader" data-sentry-source-file="index.tsx" />
        <ReactFlow proOptions={{
        hideAttribution: true
      }}
      // origin of the node at the center
      nodeOrigin={[0.5, 0.5]} onPointerMove={handleMouseMove} onPointerLeave={handleMouseLeave} onPaneClick={handlePaneClick} onPaneMouseMove={handlePaneMouseMove} nodes={localNodes.concat(ghostNode ? [ghostNode] : [])} nodeTypes={nodeTypes} onNodeDragStart={handleNodeDragStart} onNodeDragStop={handleNodeDragStop} onNodesChange={onNodesChange} onNodeClick={handleNodeClick} onNodesDelete={handleNodeDelete} onEdgeClick={handleEdgeClick}
      // DO NOT rely on the onSelectionChange event here
      // in multiplayer setup we have, when others select the node, "select" is synced here
      // and thus triggers the onSelectionChange event unexpectedly
      // onSelectionChange={handleSelectionChange}
      edges={localEdges} edgeTypes={edgeTypes} onEdgesChange={onEdgesChange} onReconnect={onReconnect} onConnect={onConnect} fitView minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} autoPanOnConnect={!isViewOnly} autoPanOnNodeDrag={!isViewOnly} elementsSelectable nodesDraggable={!isViewOnly && ghostNode === null} nodesConnectable={!isViewOnly} nodesFocusable edgesFocusable edgesReconnectable={!isViewOnly} panOnScroll panOnScrollMode={PanOnScrollMode.Free} panOnDrag={ghostNode === null} zoomOnDoubleClick={false} selectionOnDrag={ghostNode === null} deleteKeyCode={DELETE_KEY_CODE} selectionMode={SelectionMode.Partial} multiSelectionKeyCode='Shift' connectionLineStyle={{
        strokeWidth: 2,
        strokeDasharray: "4 4"
      }} data-sentry-element="ReactFlow" data-sentry-source-file="index.tsx">
          <Background size={1.6} gap={15} data-sentry-element="Background" data-sentry-source-file="index.tsx" />
          <PanelControls data-sentry-element="PanelControls" data-sentry-source-file="index.tsx" />
          <CursorPresence data-sentry-element="CursorPresence" data-sentry-source-file="index.tsx" />
        </ReactFlow>
      </QueryClientProvider>
    </div>;
}